import { type ScanPartApprovalStatus } from '@kaiizen/kai-pro-api';

import {
  getPartCostValue,
  isScopeNavigatorTask,
  type ScopeNavigatorContext,
  taskMatchesContractor,
} from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import type { ScanGroupOptimized, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';

export const getGroupOverviewTasks = (group: ScanGroupOptimized): ScanTaskOptimized[] => (group.tasks ?? []).filter(isScopeNavigatorTask);

export const getGroupOverviewContractorNames = (
  tasks: readonly ScanTaskOptimized[],
  contractorsMap?: Record<string, { name?: string }>,
): string[] => [
  ...new Set(
    tasks
      .map(task => (task.assignedContractorId ? contractorsMap?.[task.assignedContractorId]?.name : undefined))
      .filter((name): name is string => Boolean(name)),
  ),
];

/**
 * Whether a task is part of the review the current scan is asking for.
 *
 * During a change order only the tasks that changed vs the approved baseline are
 * up for a verdict; everything else is carried forward as-is (auto-approved by
 * `isScanPartApproved`) and gets no approve control or badge at all. Outside a
 * change order every task participates.
 */
export const taskParticipatesInReview = (
  taskId: string,
  scanIsChangeOrder: boolean,
  changedScopedTaskIds: readonly string[],
): boolean => !scanIsChangeOrder || changedScopedTaskIds.includes(taskId);

/** Scoped tasks that participate in review — keyed in ScanEditingContext.approvalStatusByTaskId. */
export const getGroupOverviewReviewableTasks = (
  tasks: readonly ScanTaskOptimized[],
  approvalStatusByTaskId?: Record<string, ScanPartApprovalStatus | undefined>,
): ScanTaskOptimized[] => tasks.filter(task => approvalStatusByTaskId?.[task.id] !== undefined);

/** Tasks still awaiting approval, derived from ScanEditingContext.approvalStatusByTaskId. */
export const getGroupOverviewUnreviewedTasks = (
  tasks: readonly ScanTaskOptimized[],
  approvalStatusByTaskId?: Record<string, ScanPartApprovalStatus | undefined>,
): ScanTaskOptimized[] =>
  getGroupOverviewReviewableTasks(tasks, approvalStatusByTaskId)
    .filter(task => {
      const status = approvalStatusByTaskId?.[task.id];
      return status === 'needsReview' || status === 'editResolved';
    });

/** Tasks with a completed review verdict (approved or an edit request), derived from ScanEditingContext.approvalStatusByTaskId. */
export const getGroupOverviewReviewedTasks = (
  tasks: readonly ScanTaskOptimized[],
  approvalStatusByTaskId?: Record<string, ScanPartApprovalStatus | undefined>,
): ScanTaskOptimized[] =>
  getGroupOverviewReviewableTasks(tasks, approvalStatusByTaskId)
    .filter(task => {
      const status = approvalStatusByTaskId?.[task.id];
      return status === 'approved' || status === 'editRequested';
    });

export const getFullScopeOverviewTasks = (groups: readonly ScanGroupOptimized[]): ScanTaskOptimized[] => groups.flatMap(getGroupOverviewTasks);

export const getFullScopeOverviewUnreviewedTasks = (
  groups: readonly ScanGroupOptimized[],
  approvalStatusByTaskId?: Record<string, ScanPartApprovalStatus | undefined>,
): ScanTaskOptimized[] => getGroupOverviewUnreviewedTasks(getFullScopeOverviewTasks(groups), approvalStatusByTaskId);

export const getFullScopeOverviewTotalCost = (groups: readonly ScanGroupOptimized[]): number =>
  groups.reduce((sum, group) => sum + (getPartCostValue(group) ?? 0), 0);

/**
 * A contractor's own tasks, gathered across every group — a contractor is not a
 * real scan part, so its tasks can't be read off one `group.tasks` the way a
 * group's own overview can.
 */
export const getContractorOverviewTasks = (
  groups: readonly ScanGroupOptimized[],
  contractorId: string,
  context?: ScopeNavigatorContext,
): ScanTaskOptimized[] => getFullScopeOverviewTasks(groups).filter(task => taskMatchesContractor(task, contractorId, context));

export const getContractorOverviewTotalCost = (tasks: readonly ScanTaskOptimized[]): number =>
  tasks.reduce((sum, task) => sum + (getPartCostValue(task) ?? 0), 0);
